// ============================================================
// OAMATECH ERP PRO - Enrutador principal
// ============================================================
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAutoLogout } from './hooks/useAutoLogout';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import RutaProtegida from './components/layout/RutaProtegida';
import ErrorBoundary from './components/ErrorBoundary';
import MainLayout from './components/layout/MainLayout';
import { Loader2 } from 'lucide-react';

// Carga diferida de páginas (mejor rendimiento)
const LoginPage       = lazy(() => import('./pages/auth/LoginPage'));
const DashboardPage   = lazy(() => import('./pages/dashboard/DashboardPage'));
const ReparacionesPage = lazy(() => import('./pages/repairs/ReparacionesPage'));
const ClientesPage    = lazy(() => import('./pages/clients/ClientesPage'));
const InventarioPage  = lazy(() => import('./pages/inventory/InventarioPage'));
const AlmacenPage     = lazy(() => import('./pages/almacen/AlmacenPage'));
const RecuentoPage    = lazy(() => import('./pages/recuento/RecuentoPage'));
const VentasPage      = lazy(() => import('./pages/sales/VentasPage'));
const CajaPage        = lazy(() => import('./pages/billing/CajaPage'));
const FacturacionPage = lazy(() => import('./pages/facturacion/FacturacionPage'));
const LibroFacturasPage = lazy(() => import('./pages/libroFacturas/LibroFacturasPage'));
const MetasPage         = lazy(() => import('./pages/metas/MetasPage'));
const GarantiasPage      = lazy(() => import('./pages/garantias/GarantiasPage'));
const PresupuestosPage   = lazy(() => import('./pages/presupuestos/PresupuestosPage'));
const ExportarPage       = lazy(() => import('./pages/exportar/ExportarPage'));
const ImportarPage       = lazy(() => import('./pages/importar/ImportarPage'));
const EstadisticasPage   = lazy(() => import('./pages/estadisticas/EstadisticasPage'));
const ComprasPage        = lazy(() => import('./pages/compras/ComprasPage'));
const ComprasParticularesPage = lazy(() => import('./pages/compras/ComprasParticularesPage'));
const MayoristasPage = lazy(() => import('./pages/mayoristas/MayoristasPage'));
const FacturasCompraPage = lazy(() => import('./pages/facturascompra/FacturasCompraPage'));
const DevolucionesPage   = lazy(() => import('./pages/devoluciones/DevolucionesPage'));
const GastosPage         = lazy(() => import('./pages/gastos/GastosPage'));
const UsuariosPage       = lazy(() => import('./pages/usuarios/UsuariosPage'));
const BackupPage         = lazy(() => import('./pages/backup/BackupPage'));
const TecnicosPage       = lazy(() => import('./pages/tecnicos/TecnicosPage'));
const CitasPage          = lazy(() => import('./pages/citas/CitasPage'));
const ObjetivosPage      = lazy(() => import('./pages/objetivos/ObjetivosPage'));
const IMEIPage           = lazy(() => import('./pages/imei/IMEIPage'));
const RentabilidadPage   = lazy(() => import('./pages/rentabilidad/RentabilidadPage'));
const TiendasPage        = lazy(() => import('./pages/tiendas/TiendasPage'));
const AuditoriaPage      = lazy(() => import('./pages/auditoria/AuditoriaPage'));
const ConfiguracionPage  = lazy(() => import('./pages/configuracion/ConfiguracionPage'));

// Página genérica para módulos en desarrollo
function EnDesarrollo({ nombre }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
      <div className="text-5xl mb-4">🚧</div>
      <p className="font-medium text-gray-600">{nombre}</p>
      <p className="text-sm mt-1">Módulo en desarrollo — próxima entrega</p>
    </div>
  );
}

// Auto logout wrapper
function AutoLogoutWrapper({ children }) {
  useAutoLogout(8 * 60 * 60 * 1000); // 8 horas
  return children;
}

// Spinner de carga
function Cargando() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={28} className="animate-spin text-blue-600" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: { borderRadius: '12px', fontSize: '14px' }
          }}
        />
        <Suspense fallback={<Cargando />}>
          <Routes>

            {/* Ruta pública */}
            <Route path="/login" element={<LoginPage />} />

            {/* Rutas protegidas con layout */}
            <Route
              path="/"
              element={
                <RutaProtegida>
                  <AutoLogoutWrapper>
                    <MainLayout />
                  </AutoLogoutWrapper>
                </RutaProtegida>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />

              <Route path="dashboard"    element={<DashboardPage />} />
              <Route path="reparaciones" element={<ErrorBoundary><ReparacionesPage /></ErrorBoundary>} />
              <Route path="clientes"     element={<ClientesPage />} />
              <Route path="inventario"   element={<InventarioPage />} />
              <Route path="almacen"      element={<AlmacenPage />} />
              <Route path="recuento"     element={<ErrorBoundary><RecuentoPage /></ErrorBoundary>} />
              <Route path="ventas"       element={<VentasPage />} />

              <Route path="caja"
                element={
                  <RutaProtegida roles={['admin']}>
                    <CajaPage />
                  </RutaProtegida>
                }
              />

              <Route path="garantias"      element={<GarantiasPage />} />
              <Route path="presupuestos"   element={<PresupuestosPage />} />
              <Route path="compras"         element={<ComprasPage />} />
              <Route path="compra-dispositivos" element={<ComprasParticularesPage />} />
              <Route path="mayoristas" element={<MayoristasPage />} />
              <Route path="facturas-compra" element={<RutaProtegida roles={['admin']}><FacturasCompraPage /></RutaProtegida>} />
              <Route path="devoluciones"    element={<DevolucionesPage />} />
              <Route path="gastos"          element={<GastosPage />} />
              <Route path="tecnicos"        element={<TecnicosPage />} />
              <Route path="citas"           element={<CitasPage />} />
              <Route path="objetivos"       element={<ObjetivosPage />} />
              <Route path="imei"            element={<IMEIPage />} />
              <Route path="rentabilidad"     element={<RentabilidadPage />} />
              <Route path="tiendas"         element={<TiendasPage />} />
              <Route path="usuarios"        element={<UsuariosPage />} />
              <Route path="backup"          element={<BackupPage />} />
              <Route path="estadisticas"    element={<EstadisticasPage />} />
              <Route path="auditoria"        element={<AuditoriaPage />} />
              <Route path="configuracion"    element={<ConfiguracionPage />} />
              <Route path="exportar"        element={<ExportarPage />} />
              <Route path="importar"        element={<ImportarPage />} />
              <Route path="facturacion"   element={<RutaProtegida roles={['admin']}><FacturacionPage /></RutaProtegida>} />
              <Route path="libro-facturas" element={<RutaProtegida roles={['admin']}><ErrorBoundary><LibroFacturasPage /></ErrorBoundary></RutaProtegida>} />
              <Route path="metas" element={<RutaProtegida roles={['admin']}><ErrorBoundary><MetasPage /></ErrorBoundary></RutaProtegida>} />
              <Route path="tiendas"       element={<EnDesarrollo nombre="Gestión de Tiendas" />} />

            </Route>

            {/* Redirigir rutas desconocidas */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
